import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, AlertTriangle, ShoppingBag, Search } from 'lucide-react';
import { Order, fetchAllOrders, orderReference } from '../../services/orders';
import OrderDetailDrawer from './OrderDetailDrawer';

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  processing: 'bg-blue-100 text-blue-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-200 text-gray-600',
  refunded: 'bg-red-100 text-red-700',
};

export default function OrdersPanel() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<Order | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setOrders(await fetchAllOrders());
    } catch (err: any) {
      setError(err.message || 'Tellimuste laadimine ebaõnnestus');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const statuses = useMemo(() => {
    const set = new Set(orders.map((o) => o.status).filter(Boolean));
    return Array.from(set).sort();
  }, [orders]);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const cd = o.customer_data || {};
        const hay = [
          orderReference(o),
          o.esto_reference,
          cd.email,
          cd.firstName,
          cd.lastName,
          cd.phone,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, search, statusFilter]);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Otsi nime, e-posti, viite järgi..."
            className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4FC1E9]"
        >
          <option value="">Kõik staatused</option>
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-xl text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Uuenda
        </button>
        <span className="text-gray-400 text-sm self-center ml-auto hidden sm:block">
          {filtered.length} / {orders.length} tellimust
        </span>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          Laen tellimusi...
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3 font-semibold">Viide</th>
                  <th className="text-left px-4 py-3 font-semibold">Kuupäev</th>
                  <th className="text-left px-4 py-3 font-semibold">Klient</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">E-post</th>
                  <th className="text-right px-4 py-3 font-semibold">Summa</th>
                  <th className="text-center px-4 py-3 font-semibold">Staatus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((o) => {
                  const cd = o.customer_data || {};
                  const name = `${cd.firstName || ''} ${cd.lastName || ''}`.trim() || '—';
                  return (
                    <tr
                      key={o.id}
                      onClick={() => setSelected(o)}
                      className="cursor-pointer hover:bg-[#4FC1E9]/5 transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        #{orderReference(o)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(o.created_at).toLocaleDateString('et-EE')}
                      </td>
                      <td className="px-4 py-3 text-gray-900">{name}</td>
                      <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{cd.email || '—'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                        {o.amount.toFixed(2)}€
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                            STATUS_STYLES[o.status] || 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center h-48 text-gray-500">
              <ShoppingBag className="w-12 h-12 mb-3 opacity-30" />
              <p>Tellimusi ei leitud</p>
            </div>
          )}
        </div>
      )}

      {selected && (
        <OrderDetailDrawer
          order={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
          onDeleted={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
